import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../hooks/useTranslation';
import BuySugarModal from './BuySugarModal';
import SugarTruckConfirmModal from './SugarTruckConfirmModal';

const LoadDetailsModal = ({ load, onClose, onBookSugarOnly, onBookBundle }) => {
  const { t } = useTranslation();
  const [insuranceAdded, setInsuranceAdded] = useState(false);
  const [isBuySugarModalOpen, setIsBuySugarModalOpen] = useState(false);
  const [isSugarTruckModalOpen, setIsSugarTruckModalOpen] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const handleBookSugar = () => {
    setIsBuySugarModalOpen(true);
  };

  const handleBookBundle = () => {
    setIsSugarTruckModalOpen(true);
  };

  const handleBuySugarConfirm = (purchaseData) => {
    onBookSugarOnly(purchaseData);
    setIsBuySugarModalOpen(false);
    onClose();
  };

  const handleSugarTruckConfirm = (bundleData) => {
    onBookBundle(bundleData);
    setIsSugarTruckModalOpen(false);
    onClose();
  };

  // Calculate amounts with proper GST
  const subtotal = load?.rate * load?.quantity;
  const gstRate = 0.05; // 5% GST for sugar
  const gstAmount = Math.round(subtotal * gstRate);
  const transitInsuranceRate = 0.005; // 0.5% insurance
  const transitInsuranceAmount = Math.round(subtotal * transitInsuranceRate);
  
  const totalAmount = subtotal + gstAmount + (insuranceAdded ? transitInsuranceAmount : 0);

  const handleInsuranceToggle = () => {
    setInsuranceAdded(!insuranceAdded);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1300] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-white">Sugar+Truck Bundle Details</h2>
              <p className="text-sm text-blue-100 mt-1">{load?.offerId}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors text-white text-xl font-bold"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Product Information */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Product Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Product:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.product}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Grade:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.grade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Season:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.season}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Pack Size:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.packSize}</span>
                    </div>
                  </div>
                </div>

                {/* Mill Information */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Mill Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Mill Name:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.millName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Mill Code:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.millCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Zone:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.zone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Location:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{load?.deliveryFrom}</span>
                    </div>
                  </div>
                </div>

                {/* Important Dates */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-800">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Key Dates</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Payment:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{formatDate(load?.paymentDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Lifting:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{formatDate(load?.liftingDate)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Pricing */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border-2 border-green-200 dark:border-green-800">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Pricing Breakdown</h3>
                  <div className="space-y-3">
                    <div className="text-center pb-3 border-b border-gray-300 dark:border-gray-600">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Rate per Ton</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(load?.rate)}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">+ 5% GST</p>
                    </div>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Available Quantity:</span>
                        <span className="font-bold text-gray-900 dark:text-white">{load?.quantity} MT</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600">
                        <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-blue-600 dark:text-blue-400">
                        <span className="text-xs">GST (5%):</span>
                        <span className="text-sm font-semibold">+{formatCurrency(gstAmount)}</span>
                      </div>
                      {insuranceAdded && (
                        <div className="flex justify-between text-green-600 dark:text-green-400">
                          <span className="text-xs">Insurance (0.5%):</span>
                          <span className="text-sm font-semibold">+{formatCurrency(transitInsuranceAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t-2 border-gray-300 dark:border-gray-600">
                        <span className="text-base font-bold text-gray-900 dark:text-white">Total:</span>
                        <span className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Truck Availability */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Bundle Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Trucks Available:</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        load?.trucksAvailable > 0 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {load?.trucksAvailable} Units
                      </span>
                    </div>
                    {load?.insuranceAvailable && (
                      <div className="flex items-center justify-between pt-3 border-t border-gray-300 dark:border-gray-600">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Transit Insurance:</span>
                        <Button
                          size="sm"
                          onClick={handleInsuranceToggle}
                          className="text-xs font-semibold bg-black text-white hover:bg-gray-800 border-black"
                        >
                          {insuranceAdded ? '✓ Added' : '+ Add'}
                        </Button>
                      </div>
                    )}
                    {insuranceAdded && (
                      <div className="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-2 text-xs text-green-800 dark:text-green-300">
                        <p className="font-bold">✓ Insurance Added</p>
                        <p className="text-xs">Coverage: ₹25L+ | Premium: {formatCurrency(transitInsuranceAmount)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4">
                  <p className="text-xs font-bold text-amber-900 dark:text-amber-100 mb-2">Payment Options:</p>
                  <ul className="text-xs text-amber-900 dark:text-amber-100 space-y-1">
                    <li>• <strong>Advance payment</strong> at booking to lock price and reserve load</li>
                    <li>• <strong>Balance payment</strong> after invoice generation</li>
                    <li>• <strong>Full payment</strong> before vehicle leaves factory gate</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex gap-3">
              <Button
                variant="default"
                size="lg"
                onClick={handleBookSugar}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Buy Sugar Only
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={handleBookBundle}
                disabled={load?.trucksAvailable === 0}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold disabled:bg-gray-400"
              >
                Confirm Sugar+Truck
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Buy Sugar Modal */}
      {isBuySugarModalOpen && (
        <BuySugarModal
          load={load}
          onClose={() => setIsBuySugarModalOpen(false)}
          onConfirm={handleBuySugarConfirm}
        />
      )}

      {/* Sugar+Truck Confirmation Modal */}
      {isSugarTruckModalOpen && (
        <SugarTruckConfirmModal
          load={load}
          onClose={() => setIsSugarTruckModalOpen(false)}
          onConfirm={handleSugarTruckConfirm}
        />
      )}
    </>
  );
};

export default LoadDetailsModal;